import { useState, useEffect } from "react";
import DateSelector from "../../components/shared/fields/DateSelector";
import Datatable from "../../components/shared/datatable/Datatable";
import Button from "../../components/shared/buttons/button";
import CommonModal from "../../components/shared/modalLayout/CommonModal";
import InputWithLabel from "../../components/shared/fields/InputWithLabel";
import SelectBox from "../../components/shared/fields/SelectBox ";
import { Col, Row } from "react-bootstrap";
import useTranslator from "../../hooks/useTranslator";
import { convertToUserTimezone, getCurrentDate, makePriorLotNumber } from "../../helpers/Utils";
import { DEFAULT_DATETIME_FORMAT, PASTING_BOX_WEIGHT_IN_POUND, POUND_TO_KG } from "../../helpers/constants";
import { useGetPastingLogsQuery, useUpdatePastingLogMutation, useGetPastingSummaryQuery } from "./mixingApi";
import GridCard from "../../components/shared/gridCard/GridCard";
import KgGreen from "../../images/kg_green.svg";

const STATUS_OPTIONS = [
  { value: "mixing", label: "Mixing" },
  { value: "packed", label: "Packed" },
];

const STATUS_LABELS = { mixing: "Mixing", packed: "Packed" };

const initialForm = {
  date:                  getCurrentDate("YYYY-MM-DD"),
  batch_code:            "",
  total_weight:          "",
  packed_weight:         "",
  unpacked_weight:       "",
  total_boxes:           "",
  lot_completion_percent:"",
  moisture_percent:      "",
  water_activity:        "",
  status:                "mixing",
};

const Pasting = () => {
  const { _t } = useTranslator();
  const currentDate = getCurrentDate("YYYY-MM-DD");
  const [date, setDate] = useState(currentDate);
  const [lotNumber, setLotNumber] = useState(makePriorLotNumber(currentDate));
  const [page, setPage] = useState(1);

  useEffect(() => { setLotNumber(makePriorLotNumber(date)); }, [date]);
  const [showModal, setShowModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [formData, setFormData] = useState(initialForm);

  const { data: logsData, isLoading, isFetching } = useGetPastingLogsQuery({ page, date, batch_code: lotNumber, per_page: 10 }, { refetchOnMountOrArgChange: true });
  const [updatePastingLog, { isLoading: isUpdating }] = useUpdatePastingLogMutation();
  const { data: summaryData } = useGetPastingSummaryQuery({ batch_code: lotNumber }, { skip: !lotNumber });

  const listItems = [
    { label: _t("total_weight", "Total Weight"), value: summaryData?.data?.total_weight ?? 0, icon: KgGreen },
    { label: _t("packed_weight", "Weight Packed"), value: summaryData?.data?.packed_weight ?? 0, icon: KgGreen },
    { label: _t("unpacked_weight", "Weight Not Packed"), value: summaryData?.data?.unpacked_weight ?? 0, icon: KgGreen },
    { label: _t("total_boxes", "Total Boxes"), value: summaryData?.data?.total_boxes ?? 0, icon: KgGreen },
  ];

  const handleEdit = (row) => {
    setSelectedRecord(row);
    setFormData({
      date:                   row.date                    || currentDate,
      batch_code:             row.batch_code              || "",
      total_weight:           row.total_weight            || "",
      packed_weight:          row.packed_weight           || "",
      unpacked_weight:        row.unpacked_weight         || "",
      total_boxes:            row.total_boxes             || "",
      lot_completion_percent: row.lot_completion_percent  || "",
      moisture_percent:       row.moisture_percent        || "",
      water_activity:         row.water_activity          || "",
      status:                 row.status                  || "mixing",
    });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => {
      const updated = { ...p, [name]: value };
      const total    = parseFloat(updated.total_weight)    || 0;
      const packed   = parseFloat(updated.packed_weight)   || 0;
      const unpacked = parseFloat(updated.unpacked_weight) || 0;

      if (name === "packed_weight") {
        const max = total - (parseFloat(p.unpacked_weight) || 0);
        if (packed > max) updated.packed_weight = max >= 0 ? String(max) : "0";
      }
      if (name === "unpacked_weight") {
        const max = total - (parseFloat(p.packed_weight) || 0);
        if (unpacked > max) updated.unpacked_weight = max >= 0 ? String(max) : "0";
      }
      return updated;
    });
  };

  useEffect(() => {
    const total  = parseFloat(formData.total_weight)  || 0;
    const packed = parseFloat(formData.packed_weight) || 0;
    const pct = total > 0 ? parseFloat(((packed / total) * 100).toFixed(2)) : 0;
    setFormData((p) => ({
      ...p,
      lot_completion_percent: pct
    }));
  }, [formData.packed_weight, formData.unpacked_weight, formData.total_weight]);



  // ############# Handle Packed Boxes Calculations #############
  useEffect(() => {
    const total_weight = parseFloat(formData.total_weight) || 0;
    const total_boxes = parseFloat(formData.total_boxes) || 0;
    const packed_weight =  total_boxes * (PASTING_BOX_WEIGHT_IN_POUND * POUND_TO_KG);
    const unpacked_weight =  total_weight - packed_weight;
    
    setFormData((prev) => ({
      ...prev,
      packed_weight: parseFloat(packed_weight).toFixed(2),
      unpacked_weight : parseFloat(unpacked_weight).toFixed(2)
    }));
    
  },[formData.total_boxes, formData.total_weight]);



  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isUpdating) return;
    const payload = {
      ...formData,
      total_weight:           parseFloat(formData.total_weight)           || 0,
      packed_weight:          parseFloat(formData.packed_weight)          || 0,
      unpacked_weight:        parseFloat(formData.unpacked_weight)        || 0,
      total_boxes:            parseFloat(formData.total_boxes)            || 0,
      lot_completion_percent: parseFloat(formData.lot_completion_percent) || 0,
      moisture_percent:       parseFloat(formData.moisture_percent)       || 0,
      water_activity:         parseFloat(formData.water_activity)         || 0,
    };
    try {
      await updatePastingLog({ id: selectedRecord.id, requestBody: payload }).unwrap();
      setShowModal(false);
    } catch (error) { console.error("Error saving pasting log:", error); }
  };

  const columns = [
    { label: _t("entered_date", "Entered Date"), accessor: "created_at", cell: ({ value }) => value ? convertToUserTimezone(value, DEFAULT_DATETIME_FORMAT) : "" },
    { label: _t("batch_code", "Batch Code"), accessor: "batch_code", cell: ({ value }) => value ? `#${value}` : "" },
    { label: _t("total_weight", "Total Weight"), accessor: "total_weight" },
    { label: _t("packed_weight", "Weight Packed"), accessor: "packed_weight", cell: ({ value }) => value ?? "---" },
    { label: _t("unpacked_weight", "Weight Not Packed"), accessor: "unpacked_weight", cell: ({ value }) => value ?? "---" },
    { label: _t("total_boxes", "No of Boxes"), accessor: "total_boxes", cell: ({ value }) => value ?? "---" },
    { label: _t("lot_completion_percent", "Lot Completion %"), accessor: "lot_completion_percent", cell: ({ value }) => value ?? "---" },
    {
      label: _t("status", "Status"), accessor: "status",
      cell: ({ value }) => (
        <span className={value === "packed" ? "cmn_badge" : "cmn_badge inactive"}>
          {STATUS_LABELS[value] || value}
        </span>
      ),
    },
    { label: _t("status_updated_at", "Status Updated At"), accessor: "status_updated_at", cell: ({ value }) => value ? convertToUserTimezone(value, DEFAULT_DATETIME_FORMAT) : "" },
    {
      label: _t("action", "Action"), accessor: "id",
      cell: ({ row }) => <Button size="small" className={row.status === "packed" ? "borderRedBtn" : "" } label={row.status === "packed" ? _t("view", "View") : _t("edit", "Edit")} onClick={() => handleEdit(row)} />,
    },
  ];

  return (
    <div>
      <CommonModal show={showModal} handleClose={() => setShowModal(false)} setShow={setShowModal}
        title={selectedRecord?.status === "packed" ? _t("view_pasting_log", "View Pasting Log") : _t("edit_pasting_log", "Edit Pasting Log")}>
        {(() => {
          const isPacked = selectedRecord?.status === "packed";
          return (
            <>
              <Row>
                <Col lg={6}><InputWithLabel label={_t("date", "Date")} type="date" name="date" value={formData.date} onChange={handleChange} disabled={true} /></Col>
                <Col lg={6}><InputWithLabel label={_t("batch_code", "Batch Code")} type="text" name="batch_code" value={formData.batch_code} onChange={handleChange} disabled={true} /></Col>
                <Col lg={6}><InputWithLabel label={_t("total_weight", "Total Weight")} type="number" name="total_weight" value={formData.total_weight} onChange={handleChange} placeholder="0" disabled={true} /></Col>
                <Col lg={6}><InputWithLabel label={_t("total_boxes", "Total Boxes")} type="number" name="total_boxes" value={formData.total_boxes} onChange={handleChange} placeholder="0" disabled={isPacked} /></Col>
                <Col lg={6}><InputWithLabel label={_t("packed_weight", "Packed Weight")} type="number" name="packed_weight" value={formData.packed_weight} onChange={handleChange} placeholder="0" disabled={isPacked} /></Col>
                <Col lg={6}><InputWithLabel label={_t("unpacked_weight", "Unpacked Weight")} type="number" name="unpacked_weight" value={formData.unpacked_weight} onChange={handleChange} placeholder="0" disabled={isPacked} /></Col>
                <Col lg={6}><InputWithLabel label={_t("moisture_percent", "Moisture %")} type="number" name="moisture_percent" value={formData.moisture_percent} onChange={handleChange} placeholder="0" disabled={isPacked} /></Col>
                <Col lg={6}><InputWithLabel label={_t("water_activity", "Water Activity")} type="number" name="water_activity" value={formData.water_activity} onChange={handleChange} placeholder="0" disabled={isPacked} /></Col>
                <Col lg={6}><InputWithLabel label={_t("lot_completion_percent", "Lot Completion %")} type="number" name="lot_completion_percent" value={formData.lot_completion_percent} onChange={handleChange} placeholder="0" disabled={true} /></Col>
                <Col lg={6}><SelectBox label={_t("status", "Status")} name="status" value={formData.status} onChange={handleChange} options={STATUS_OPTIONS} disabled={isPacked} /></Col>
              </Row>
              {!isPacked && (
                <Row>
                  <Col className="text-end">
                    <Button label={_t("save_changes", "Save Changes")} size="small" onClick={handleSubmit} disabled={isUpdating} />
                  </Col>
                </Row>
              )}
            </>
          );
        })()}
      </CommonModal>

      <div className="overview cmn_card mb-3">
        <div className="overview_header d-flex gap-2 align-items-center">
          <h6 className="mb-0 flex-grow-1">{_t("pasting_logs", "Pasting Logs")}</h6>
          <div className="d-flex gap-2 align-items-center">
            <h6 className="mb-0">{_t("batch_code", "Batch Code")}</h6>
            <Button label={`#${lotNumber ?? 0}`} size="small" className="borderRedBtn" />
            <DateSelector setDate={setDate} />
          </div>
        </div>
        <ul className="m-0 grid_card_wrapper">
          {listItems.map((list, index) => (
            <GridCard key={index} title={list.label} value={list.value} icon={list.icon} />
          ))}
        </ul>
      </div>

      <Datatable
        title={_t("pasting_logs", "Pasting Logs")}
        columns={columns}
        tableData={logsData?.data}
        isLoading={isLoading || isFetching}
        showPegination={true}
        page={page}
        onPageChange={setPage}
        showCount={true}
        pageLimit={10}
      />
    </div>
  );
};

export default Pasting;
